import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, openDownload } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SedeSelect } from "@/components/SedeSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CallButton, CallLog } from "@/components/Romulus";
import { ArrowLeft, Pencil, User, Phone, Mail, CalendarClock, History, MessageCircle, Bell, Layers, ChevronRight, FilePlus, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useFileNamePrompt, renamedFile } from "@/components/FileNamePrompt";
import { SmsButton } from "@/components/SmsButton";

const FIELDS = [
  ["codice_fiscale", "Codice Fiscale"], ["cellulare", "Cellulare"], ["telefono", "Telefono fisso"],
  ["email", "E-Mail"], ["datore_lavoro", "Datore di Lavoro"], ["professione", "Professione"],
  ["data_nascita", "Data di nascita"], ["residenza", "Residenza"], ["indirizzo", "Indirizzo"],
  ["provincia", "Provincia"], ["cap", "Cap"], ["importo_richiesto", "Importo Richiesto"],
  ["bene_finanziato", "Bene Finanziato"], ["rata", "Rata"], ["durata", "Durata (mesi)"], ["cluster", "Cluster"],
  ["convenzionato", "Convenzionato"], ["codice_cliente", "Codice Cliente"], ["sede_label", "Sede (file)"],
  ["original_id", "ID origine"],
];

const ESITO_COLOR = {
  "Da contattare": "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  "Non risponde": "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
  "Richiamare": "bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300",
  "In lavorazione": "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300",
  "Non interessato": "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300",
  "Appuntamento fissato": "bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300",
  "Appuntamento Telefonico": "bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-300",
  "Numero errato": "bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-300",
};

const EDIT_FIELDS = [["nome", "Nome"], ["cognome", "Cognome"], ...FIELDS];

export default function LeadDetail() {
  const { cid, lid } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [esiti, setEsiti] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [assignVal, setAssignVal] = useState("none");
  const [form, setForm] = useState({ esito: "", nota: "", event_date: "", sede_id: "", prossima_attivita: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [askName, nameDialog] = useFileNamePrompt();
  const renameDoc = async (d) => {
    const name = await askName(d.original_filename, "Rinomina file");
    if (!name || name === d.original_filename) return;
    try { await api.patch(`/leads/${lid}/documents/${d.id}`, { name }); toast.success("File rinominato"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore"); }
  };

  const uploadFile = async (e) => {
    let file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const name = await askName(file.name);
    if (!name) return;
    file = renamedFile(file, name);
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try { await api.post(`/leads/${lid}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File allegato"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore upload"); }
    finally { setUploading(false); }
  };
  const deleteDoc = async (docId) => {
    if (!window.confirm("Eliminare l'allegato?")) return;
    try { await api.delete(`/leads/${lid}/documents/${docId}`); toast.success("Allegato eliminato"); load(); }
    catch (e) { toast.error("Errore"); }
  };

  const load = useCallback(() => {
    api.get(`/leads/${lid}`, { params: cid ? { campaign_id: cid } : {} }).then((r) => { setLead(r.data); setForm((f) => ({ ...f, esito: r.data.esito || "Da contattare", prossima_attivita: r.data.prossima_attivita || "" })); setAssignVal(r.data.assigned_to || "none"); })
      .catch(() => { toast.error("Lead non trovato"); navigate(cid ? `/marketing/${cid}` : "/marketing/rubrica"); });
  }, [lid, cid, navigate]);

  useEffect(() => { load(); api.get("/meta").then((r) => setEsiti(r.data.lead_esiti)); api.get("/users/internal").then((r) => setInternalUsers(r.data)); }, [load]);

  const assign = async (v) => {
    setAssignVal(v);
    try { await api.put(`/leads/${lid}/assign`, { user_id: v === "none" ? null : v }); toast.success("Assegnazione aggiornata"); load(); }
    catch (e) { toast.error("Errore"); }
  };

  const savePA = async (v) => {
    try { await api.put(`/leads/${lid}`, { prossima_attivita: v || "" }); toast.success("Prossima attività aggiornata"); }
    catch (e) { toast.error("Errore"); }
  };

  const saveEsito = async () => {
    try {
      await api.put(`/leads/${lid}/esito`, { esito: form.esito, nota: form.nota, event_date: form.event_date || null, sede_id: form.sede_id || null, campaign_id: cid });
      toast.success("Esito aggiornato"); setForm((f) => ({ ...f, nota: "", event_date: "", sede_id: "" })); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };

  const creaPratica = async () => {
    if (!window.confirm("Creare una nuova pratica da questo lead? Verrà creata o collegata l'anagrafica del cliente.")) return;
    try {
      const { data } = await api.post(`/leads/${lid}/crea-pratica`);
      toast.success(`Pratica ${data.numero} creata`);
      navigate(`/pratiche/${data.practice_id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };

  const openEdit = () => {
    const seed = { nome: lead.nome || "", cognome: lead.cognome || "", note: lead.note || "" };
    FIELDS.forEach(([k]) => { seed[k] = lead[k] || ""; });
    setEditForm(seed);
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/leads/${lid}`, editForm);
      toast.success("Lead aggiornato");
      setEditOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore aggiornamento lead"); }
    finally { setSavingEdit(false); }
  };

  const needsDate = ["Richiamare", "Appuntamento fissato", "Appuntamento Telefonico"].includes(form.esito);
  if (!lead) return <div className="text-muted-foreground">Caricamento…</div>;
  const hasCampaign = !!cid;
  const backTarget = cid ? `/marketing/${cid}` : "/marketing/rubrica";
  const backLabel = cid ? "Torna alla campagna" : "Torna alla Rubrica";
  const fullName = `${lead.nome || ""} ${lead.cognome || ""}`.trim() || "—";
  const now = new Date();
  const nextAction = (lead.events || []).filter((e) => !e.done && new Date(e.start) >= now).sort((a, b) => new Date(a.start) - new Date(b.start))[0];
  const waDigits = (lead.cellulare || lead.telefono || "").replace(/\D/g, "");
  const waMsg = `Buongiorno ${lead.nome || ""},`.trim();
  const waLink = waDigits ? `https://wa.me/${waDigits.startsWith("39") ? waDigits : "39" + waDigits}?text=${encodeURIComponent(waMsg)}` : null;

  return (
    <div className="space-y-6">
      {nameDialog}
      <button onClick={() => navigate(backTarget)} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-campaign"><ArrowLeft className="h-4 w-4" /> {backLabel}</button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-primary">{lead.lead_code}</span>
            {hasCampaign && <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${ESITO_COLOR[lead.esito] || ESITO_COLOR["Da contattare"]}`} data-testid="lead-current-esito">{lead.esito}</span>}
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1">{fullName}</h1>
          <p className="text-sm text-muted-foreground mt-1">Campagna: {lead.campaign_name || "—"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <SmsButton recipients={[{ name: fullName, number: lead.cellulare || lead.telefono }]} defaultMessage={waMsg} context={{ type: "lead", id: lid }} />
          {waLink && <a href={waLink} target="_blank" rel="noopener noreferrer"><Button variant="outline" data-testid="lead-whatsapp-btn" className="text-emerald-600 border-emerald-300"><MessageCircle className="h-4 w-4 mr-2" />WhatsApp</Button></a>}
          {lead.cellulare && <CallButton phone={lead.cellulare} leadId={lead.id} contactName={`${lead.nome || ""} ${lead.cognome || ""}`.trim()} testid="lead-call-btn" />}
          {lead.email && <a href={`mailto:${lead.email}`}><Button variant="outline"><Mail className="h-4 w-4 mr-2" />Email</Button></a>}
          <Button variant="outline" data-testid="lead-edit-btn" onClick={openEdit}><Pencil className="h-4 w-4 mr-2" />Modifica</Button>
          <Button data-testid="lead-crea-pratica-btn" onClick={creaPratica}><FilePlus className="h-4 w-4 mr-2" />Crea pratica</Button>
        </div>
      </div>

      {nextAction && (
        <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3" data-testid="lead-next-action">
          <Bell className="h-5 w-5 text-primary shrink-0" />
          <div>
            <p className="text-sm font-semibold">Prossima azione: {nextAction.titolo}</p>
            <p className="text-xs text-muted-foreground">{fmtDateTime(nextAction.start)} · {nextAction.tipo}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <Card className="p-5" data-testid="lead-info-card">
            <h2 className="font-heading font-semibold mb-4 flex items-center gap-2"><User className="h-4 w-4 text-primary" /> Informazioni Lead</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {FIELDS.map(([k, label]) => (
                <div key={k} className="flex flex-col border-b border-border/60 pb-2">
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
                  <span className="text-sm font-medium break-words">{lead[k] || "—"}</span>
                </div>
              ))}
            </div>
            {lead.note && (
              <div className="mt-4 pt-3 border-t border-border">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Note</p>
                <p className="text-sm whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: String(lead.note).replace(/<br\s*\/?>/gi, "\n") }} />
              </div>
            )}
          </Card>

          {lead.campaigns_detail?.length > 0 && (
          <Card className="p-5" data-testid="lead-campaigns-card">
            <h2 className="font-heading font-semibold mb-3 flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /> Campagne collegate <span className="text-xs font-normal text-muted-foreground">({lead.campaigns_detail.length})</span></h2>
            <div className="space-y-3">
              {lead.campaigns_detail.map((c) => (
                <div key={c.campaign_id} className={`rounded-lg border p-3 ${c.campaign_id === cid ? "border-primary/50 bg-primary/5" : "border-border"}`} data-testid={`lead-campaign-${c.campaign_id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <button type="button" onClick={() => c.campaign_id !== cid && navigate(`/marketing/${c.campaign_id}/lead/${lid}`)} disabled={c.campaign_id === cid} className="text-sm font-semibold truncate hover:text-primary inline-flex items-center gap-1 disabled:cursor-default disabled:hover:text-foreground" data-testid={`lead-campaign-link-${c.campaign_id}`}>
                        {c.campaign_name}{c.campaign_id !== cid && <ChevronRight className="h-3.5 w-3.5" />}
                      </button>
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.attiva ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300" : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`}>{c.attiva ? "Attiva" : "Scaduta"}</span>
                      {c.campaign_id === cid && <span className="text-[10px] text-primary font-semibold">• corrente</span>}
                    </div>
                    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${ESITO_COLOR[c.esito] || ESITO_COLOR["Da contattare"]}`}>{c.esito}</span>
                  </div>
                  {c.esito_history?.length > 0 ? (
                    <div className="mt-2 space-y-1.5 border-t border-border/60 pt-2">
                      {[...c.esito_history].reverse().map((h, i) => (
                        <div key={i} className="text-xs">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">{h.esito}</span>
                            <span className="text-[10px] text-muted-foreground">{h.by} · {fmtDateTime(h.at)}</span>
                          </div>
                          {h.nota && <p className="text-muted-foreground">{h.nota}</p>}
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-xs text-muted-foreground mt-1">Nessuna nota per questa campagna</p>}
                </div>
              ))}
            </div>
          </Card>
          )}

          <CallLog leadId={lead.id} />

          {hasCampaign && (
          <Card className="p-5">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h2 className="font-heading font-semibold flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Storico Note ed Esiti</h2>
              {lead.in_campaigns_count > 1 && <span className="text-[11px] rounded-full bg-primary/10 text-primary px-2 py-0.5 font-semibold" data-testid="lead-multi-campaign">Presente in {lead.in_campaigns_count} campagne</span>}
            </div>
            {lead.other_campaigns?.length > 0 && (
              <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs" data-testid="lead-other-campaigns">
                Questo contatto è presente anche in: {lead.other_campaigns.map((c) => c.nome).join(", ")}
              </div>
            )}
            <div className="space-y-2" data-testid="lead-history-list">
              {[...(lead.cross_history || lead.esito_history || [])].reverse().map((h, i) => (
                <div key={i} className={`rounded-lg border p-3 ${h.other_campaign ? "border-amber-500/40 bg-amber-500/5" : "border-border"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">{h.esito}</span>
                    <span className="text-[11px] text-muted-foreground">{h.by} · {fmtDateTime(h.at)}</span>
                  </div>
                  {h.nota && <p className="text-xs text-muted-foreground mt-0.5">{h.nota}</p>}
                  {h.campaign_name && (
                    <p className="text-[10px] uppercase tracking-wider mt-1 flex items-center gap-1 text-muted-foreground">
                      <MessageCircle className="h-3 w-3" />Campagna: <span className={h.other_campaign ? "text-amber-600 font-semibold" : "font-medium"}>{h.campaign_name}</span>{h.other_campaign && " (altra campagna)"}
                    </p>
                  )}
                </div>
              ))}
              {(!lead.cross_history || lead.cross_history.length === 0) && (!lead.esito_history || lead.esito_history.length === 0) && <p className="text-sm text-muted-foreground py-2">Nessun esito registrato</p>}
            </div>
          </Card>
          )}
        </div>

        <div className="lg:col-span-4 space-y-6">
          <Card className="p-5 space-y-3">
            <h2 className="font-heading font-semibold flex items-center gap-2"><User className="h-4 w-4 text-primary" /> Assegnato a</h2>
            <Select value={assignVal} onValueChange={assign}>
              <SelectTrigger data-testid="lead-assign-select"><SelectValue placeholder="Non assegnato" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Non assegnato</SelectItem>
                {internalUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Card>

          {hasCampaign && (
          <Card className="p-5 space-y-3">
            <h2 className="font-heading font-semibold flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> Registra Esito Chiamata</h2>
            <Select value={form.esito} onValueChange={(v) => setForm({ ...form, esito: v })}>
              <SelectTrigger data-testid="lead-detail-esito-select"><SelectValue /></SelectTrigger>
              <SelectContent>{esiti.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            {needsDate && (
              <div className="space-y-1.5">
                <Label>Data/ora {form.esito === "Richiamare" ? "richiamo" : "appuntamento"}</Label>
                <Input type="datetime-local" data-testid="lead-detail-event-date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
              </div>
            )}
            {["Appuntamento fissato", "Appuntamento Telefonico"].includes(form.esito) && <SedeSelect testid="lead-detail-sede-select" value={form.sede_id} onChange={(v) => setForm({ ...form, sede_id: v })} />}
            <div className="space-y-1.5">
              <Label>Prossima attività (opzionale)</Label>
              <Input type="date" data-testid="lead-detail-prossima-attivita" value={form.prossima_attivita || ""} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, prossima_attivita: v })); savePA(v); }} />
            </div>
            <Textarea data-testid="lead-detail-nota" value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })} placeholder="Nota chiamata…" />
            <Button className="w-full" data-testid="lead-detail-save-esito" onClick={saveEsito}>Salva Esito</Button>
          </Card>
          )}

          <Card className="p-5">
            <h2 className="font-heading font-semibold mb-3 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> Eventi Collegati</h2>
            <div className="space-y-2">
              {(lead.events || []).map((e) => (
                <div key={e.id} className="rounded-lg border border-border p-3">
                  <p className="text-sm font-medium">{e.titolo}</p>
                  <p className="text-xs text-muted-foreground">{fmtDateTime(e.start)} · {e.tipo}{e.done ? " · completato" : ""}</p>
                </div>
              ))}
              {(!lead.events || lead.events.length === 0) && <p className="text-sm text-muted-foreground py-2">Nessun evento in calendario</p>}
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3 gap-2">
              <h2 className="font-heading font-semibold flex items-center gap-2"><FilePlus className="h-4 w-4 text-primary" /> Allegati</h2>
              <label className="inline-flex items-center gap-1.5 text-sm rounded-md border border-border px-3 py-1.5 cursor-pointer hover:bg-accent" data-testid="lead-upload-label">
                <Upload className="h-3.5 w-3.5" />{uploading ? "Caricamento…" : "Allega file"}
                <input type="file" className="hidden" data-testid="lead-upload-input" onChange={uploadFile} disabled={uploading} />
              </label>
            </div>
            <div className="space-y-2">
              {(lead.documents || []).filter((d) => !d.is_deleted).map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-2.5" data-testid={`lead-doc-${d.id}`}>
                  <button type="button" onClick={() => openDownload(`/leads/${lid}/documents/${d.id}/download`)} className="text-left text-sm text-primary truncate hover:underline" data-testid={`lead-doc-open-${d.id}`}>{d.original_filename}</button>
                  <span className="flex items-center gap-2 shrink-0">
                    <button type="button" data-testid={`lead-doc-rename-${d.id}`} onClick={() => renameDoc(d)} className="text-muted-foreground hover:text-foreground" title="Rinomina"><Pencil className="h-3.5 w-3.5" /></button>
                    <button type="button" data-testid={`lead-doc-delete-${d.id}`} onClick={() => deleteDoc(d.id)} className="text-muted-foreground hover:text-destructive shrink-0" title="Elimina allegato"><Trash2 className="h-3.5 w-3.5" /></button>
                  </span>
                </div>
              ))}
              {(!lead.documents || lead.documents.filter((d) => !d.is_deleted).length === 0) && <p className="text-sm text-muted-foreground py-2">Nessun allegato</p>}
            </div>
          </Card>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-edit-dialog">
          <DialogHeader><DialogTitle>Modifica Lead</DialogTitle><DialogDescription>Aggiorna i dati anagrafici e di contatto del lead.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            {EDIT_FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs">{label}</Label>
                <Input data-testid={`lead-edit-${k}`} value={editForm[k] ?? ""} onChange={(e) => setEditForm((p) => ({ ...p, [k]: e.target.value }))} />
              </div>
            ))}
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs">Note</Label>
              <Textarea data-testid="lead-edit-note" value={editForm.note ?? ""} onChange={(e) => setEditForm((p) => ({ ...p, note: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button data-testid="lead-edit-save" onClick={saveEdit} disabled={savingEdit}>{savingEdit ? "Salvataggio…" : "Salva modifiche"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
